/**
 * LegendRow — colour-key legend for home-vs-away widgets.
 *
 * Three byte-identical CSS blocks previously lived in
 * `IhsVictoryProbability`, `IhsSeasonStats` and `IhsOverGoals`. This
 * primitive replaces them. Renders an 8px dot + label per item; items
 * with `position: 'right'` flip the dot to the right of the label so
 * the away team's swatch sits flush with the right edge.
 *
 * Exports two pieces:
 *   - `LegendDot` — the dot alone, when you need to inline a swatch.
 *   - `LegendRow` — the two-team strip with a justify-between layout.
 *
 * Shape:
 *   { items: Array<{ label: string; color: string; position?: 'left'|'right' }> }
 */

import type { ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import styles from './LegendRow.module.css';

const dataShape = z.object({
  items: z
    .array(
      z.object({
        label: z.string(),
        color: z.string(),
        position: z.enum(['left', 'right']).optional(),
      }),
    )
    .min(1)
    .max(4),
});

export const META: PrimitiveMeta = {
  name: 'legend-row',
  description: 'Colour-key strip — coloured dot + label per item.',
  composable: true,
  dataShape,
  densities: ['compact'],
  breakpoints: { mobile: true, wide: true },
  animation: 'none',
};

interface LegendDotProps {
  color: string;
  size?: number;
}

export function LegendDot({ color, size = 8 }: LegendDotProps): ReactElement {
  return (
    <span
      className={styles.dot}
      style={{
        background: color,
        width: `${size}px`,
        height: `${size}px`,
      }}
      aria-hidden="true"
    />
  );
}

interface LegendItem {
  label: string;
  color: string;
  /** Default 'left' — dot before label. 'right' — label before dot. */
  position?: 'left' | 'right';
}

interface LegendRowProps {
  items: LegendItem[];
}

export function LegendRow({ items }: LegendRowProps): ReactElement {
  return (
    <div className={styles.legend}>
      {items.map((item, i) => {
        // Phase 3c: right-positioned items get an extra class that
        // pins the dot+label pair to the right edge of their grid cell.
        // Previously the flex `justify-content: space-between` did this
        // implicitly; the grid switch needs an explicit cell alignment.
        const itemClass =
          item.position === 'right'
            ? `${styles.item ?? ''} ${styles.itemRight ?? ''}`
            : (styles.item ?? '');
        return (
          <span key={`${item.label}-${i}`} className={itemClass}>
            {item.position === 'right' ? (
              <>
                <span className={styles.label}>{item.label}</span>
                <LegendDot color={item.color} />
              </>
            ) : (
              <>
                <LegendDot color={item.color} />
                <span className={styles.label}>{item.label}</span>
              </>
            )}
          </span>
        );
      })}
    </div>
  );
}

export default LegendRow;
